import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput, Checkbox } from "@/components/ui/Field";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import { useRealtimeSync } from "@/lib/useRealtime";
import type { AutocompleteOption } from "@/components/Autocomplete";
import type { HeroArticleSuggestion } from "@/lib/material";
import { StandardMaterialDialog } from "../../components/StandardMaterialDialog";
import { KundenZuweisenDialog } from "../../components/KundenZuweisenDialog";
import { materialArticleSource } from "../../lib/sources";
import {
  createKunde,
  createKundenStandardMaterial,
  deleteKunde,
  deleteKundenStandardMaterial,
  listKunden,
  listKundenStandardMaterials,
  updateKunde,
  updateKundenStandardMaterial,
  type KundeOut,
} from "../../lib/settingsApi";

interface KundeForm {
  code: string;
  name: string;
  use_travel_eighths: boolean;
  show_time_range_on_timesheet: boolean;
  use_hero_customer_name_in_projects: boolean;
  travel_eighths_hero_nr: string;
}

const EMPTY: KundeForm = {
  code: "",
  name: "",
  use_travel_eighths: false,
  show_time_range_on_timesheet: false,
  use_hero_customer_name_in_projects: false,
  travel_eighths_hero_nr: "",
};

export function KundenSection() {
  const [rows, setRows] = useState<KundeOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<KundeForm>(EMPTY);
  const [formBaseline, setFormBaseline] = useState<KundeForm>(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [materialKunde, setMaterialKunde] = useState<KundeOut | null>(null);
  const [assignKunde, setAssignKunde] = useState<KundeOut | null>(null);
  const [travelArticle, setTravelArticle] = useState<AutocompleteOption<HeroArticleSuggestion> | null>(null);

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const list = await listKunden();
      setRows(list.sort((a, b) => a.code - b.code));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kunden konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useRealtimeSync({
    domains: ["kunden"],
    paused: formOpen || materialKunde != null || assignKunde != null,
    onChange: () => void reload({ silent: true }),
  });

  function openCreate() {
    setEditId(null);
    setForm(EMPTY);
    setFormBaseline(EMPTY);
    setTravelArticle(null);
    setFormError(null);
    setFormOpen(true);
  }

  function openEdit(k: KundeOut) {
    setEditId(k.id);
    const initial: KundeForm = {
      code: String(k.code),
      name: k.name,
      use_travel_eighths: k.use_travel_eighths,
      show_time_range_on_timesheet: k.show_time_range_on_timesheet,
      use_hero_customer_name_in_projects: k.use_hero_customer_name_in_projects,
      travel_eighths_hero_nr: k.travel_eighths_hero_nr || "",
    };
    setForm(initial);
    setFormBaseline(initial);
    setTravelArticle(
      k.travel_eighths_hero_nr
        ? {
            key: k.travel_eighths_hero_nr,
            label: k.travel_eighths_hero_nr,
            value: {
              label: k.travel_eighths_hero_nr,
              value: k.travel_eighths_hero_nr,
              hero_nr: k.travel_eighths_hero_nr,
              artikel_id: null,
              hero_unit: null,
              einheit: null,
              ean: null,
            },
          }
        : null,
    );
    setFormError(null);
    setFormOpen(true);
  }

  async function handleSave() {
    const code = parseInt(form.code, 10);
    if (!Number.isFinite(code) || !form.name.trim()) {
      setFormError("Code und Name sind Pflichtfelder.");
      return;
    }
    const body = {
      code,
      name: form.name.trim(),
      use_travel_eighths: form.use_travel_eighths,
      show_time_range_on_timesheet: form.show_time_range_on_timesheet,
      use_hero_customer_name_in_projects: form.use_hero_customer_name_in_projects,
      travel_eighths_hero_nr:
        travelArticle?.value?.hero_nr || form.travel_eighths_hero_nr.trim() || null,
    };
    setSaving(true);
    setFormError(null);
    try {
      if (editId) await updateKunde(editId, body);
      else await createKunde(body);
      setFormOpen(false);
      await reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(k: KundeOut) {
    if (!window.confirm(`Kunde „${k.code} – ${k.name}" wirklich löschen?`)) return;
    try {
      await deleteKunde(k.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  return (
    <Card
      title="Kundenverwaltung"
      description="Ernsthaus-Kunden und Stundennachweis-Optionen."
      actions={
        <Button type="button" onClick={openCreate}>
          Neu
        </Button>
      }
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-label-secondary">
          <Spinner /> Lädt …
        </div>
      )}
      {error && (
        <Alert variant="error" title="Fehler" className="mb-3">
          {error}
        </Alert>
      )}
      {!loading && (
        <Table<KundeOut>
          rowKey={(r) => r.id}
          rows={rows}
          stickyEndColumnCount={1}
          columns={[
            {
              key: "code",
              header: "Code",
              sortable: true,
              sortValue: (r) => r.code,
              render: (r) => r.code,
            },
            {
              key: "name",
              header: "Name",
              sortable: true,
              sortValue: (r) => r.name,
              render: (r) => r.name,
            },
            {
              key: "flags",
              header: "Optionen",
              sortable: true,
              sortValue: (r) =>
                [
                  r.use_travel_eighths && "Reise-Achtel",
                  r.show_time_range_on_timesheet && "Zeitspanne",
                  r.use_hero_customer_name_in_projects && "Hero-Name",
                ]
                  .filter(Boolean)
                  .join(" · "),
              render: (r) => (
                <span className="text-xs text-label-secondary">
                  {[
                    r.use_travel_eighths && "Reise-Achtel",
                    r.show_time_range_on_timesheet && "Zeitspanne",
                    r.use_hero_customer_name_in_projects && "Hero-Name",
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </span>
              ),
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div className="flex flex-wrap justify-end gap-1">
                  <Button type="button" size="sm" variant="secondary" onClick={() => openEdit(r)}>
                    Bearbeiten
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => setAssignKunde(r)}>
                    Kunden
                  </Button>
                  <Button type="button" size="sm" variant="secondary" onClick={() => setMaterialKunde(r)}>
                    Material
                  </Button>
                  <Button type="button" size="sm" variant="danger" onClick={() => handleDelete(r)}>
                    Löschen
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        dirty={hasFormChanges(form, formBaseline)}
        title={editId ? "Kunde bearbeiten" : "Neuen Kunden hinzufügen"}
        size="lg"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={handleSave} loading={saving}>
              Speichern
            </Button>
          </>
        }
      >
        {formError && (
          <Alert variant="error" title="Fehler" className="mb-3">
            {formError}
          </Alert>
        )}
        <div className="space-y-3">
          <TextInput
            label="Code"
            type="number"
            required
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            disabled={!!editId}
          />
          <TextInput
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Checkbox
            label="Reisezeit als Achtelstunden verwenden"
            checked={form.use_travel_eighths}
            onChange={(e) => setForm((f) => ({ ...f, use_travel_eighths: e.target.checked }))}
          />
          <Checkbox
            label="Zeitspanne auf Stundennachweis anzeigen"
            checked={form.show_time_range_on_timesheet}
            onChange={(e) => setForm((f) => ({ ...f, show_time_range_on_timesheet: e.target.checked }))}
          />
          <Checkbox
            label="Hero-Kundenname in Projekttabelle verwenden"
            checked={form.use_hero_customer_name_in_projects}
            onChange={(e) =>
              setForm((f) => ({ ...f, use_hero_customer_name_in_projects: e.target.checked }))
            }
          />
          <LockedAutocomplete<HeroArticleSuggestion>
            label="Hero-Artikel für Reisezeit"
            placeholder="Artikelname oder Nummer suchen …"
            value={travelArticle}
            minChars={2}
            loadOptions={materialArticleSource}
            onChange={(opt) => {
              setTravelArticle(opt);
              if (opt?.value?.hero_nr) {
                setForm((f) => ({ ...f, travel_eighths_hero_nr: opt.value.hero_nr || "" }));
              }
            }}
          />
        </div>
      </Dialog>

      {materialKunde && (
        <StandardMaterialDialog
          open={!!materialKunde}
          onClose={() => setMaterialKunde(null)}
          title={`Standardmaterialien – ${materialKunde.code} ${materialKunde.name}`}
          api={{
            list: () => listKundenStandardMaterials(materialKunde.id),
            create: (body) => createKundenStandardMaterial(materialKunde.id, body),
            update: (id, body) => updateKundenStandardMaterial(materialKunde.id, id, body),
            remove: (id) => deleteKundenStandardMaterial(materialKunde.id, id),
          }}
        />
      )}

      <KundenZuweisenDialog
        open={!!assignKunde}
        onClose={() => setAssignKunde(null)}
        kunde={assignKunde}
      />
    </Card>
  );
}
